import React, { useRef, useState, useEffect, MouseEvent } from 'react';
import cn from 'classnames';
import { useStoreState } from 'base/store';
import { Popover } from 'antd';
import { addEvent, removeEvent } from 'ava-util/dist/dom-event';
import { INavItem } from 'mod/store/nav/interface';
import { hasSubmenu } from 'mod/store/nav/utils';
import { debounce } from 'throttle-debounce';
import { moduleId, track } from 'mod/store/nav/utils';
import { NavLink } from 'mod/store/nav/NavLink';
import { SubMenu } from './SubMenu';
import { useTrans } from 'base/utils/i18n';

type IProps = {
    className?: string;
    popupClassName?: string;
}

export const NavPC: React.FC<IProps> = ({ className, popupClassName }) => {
    const { ua: { isMobile } } = useStoreState('rootStore');
    const { navList: navListStore } = useStoreState('persistStore') || {};
    const [visible, setVisible] = useState<boolean>(false);
    const [curIndex, setCurIndex] = useState<number>(-1);//保存当前submenu对应的一级分类的索引
    const navRef = useRef<HTMLDivElement>(null) //最外层容器
    const indexRef = useRef<number>(-1);
    const { t } = useTrans();

    const close = () => setVisible(false);

    const navList = navListStore?.list || []

    const showSubmenu = () => {
        if (indexRef.current > -1) {
            setCurIndex(indexRef.current);
            setVisible(true);
        }
    };

    const hideSubmenu = () => {
        indexRef.current = -1;
        setCurIndex(-1);
        setVisible(false);
    };

    const debounceShowSubmenu = debounce(200, showSubmenu);

    const handleMouseEnter = (index: number) => {
        if (hasSubmenu(navList[index])) {
            indexRef.current = index;
            if (visible) {
                showSubmenu()
            } else {
                debounceShowSubmenu();
            }
        } else {
            hideSubmenu();
        }
    };

    const handleMobileClick = (e: React.MouseEvent, index: number) => {
        const item = navList[index];

        if (!hasSubmenu(item)) {
            hideSubmenu();
            return;
        }

        if (!visible) {
            setVisible(true);
        }
        if (curIndex !== index || !visible) {
            e.preventDefault();
        }
        if (curIndex === index) {
            setVisible(false);
            setCurIndex(-1);
        } else {
            setCurIndex(index);
        }
    };

    useEffect(() => {
        track({
            action: 'impr',
            target: 'menu',
            relTarget: 'menu',
        });

        addEvent(window, 'resize', close);
        addEvent(window, 'scroll', close);
        return () => {
            removeEvent(window, 'resize', close);
            removeEvent(window, 'scroll', close);
        };
    }, []);


    return navList?.length === 0 ? null : (
        <div
            className={cn(className, 'page-wrapper text-center relative')}
            ref={navRef}
            data-t-mod={moduleId}
        >
            <h2 className={'visually-hidden'}>{t('web_shop_navi_shop_by_category')}</h2>

            {navList && navList?.length && (
                <Popover
                    content={navList[curIndex] ? (
                        <SubMenu
                            lv1Item={navList[curIndex]}
                            lv1Index={curIndex}
                            onClose={close}
                            visible={visible}
                        />
                    ) : null}
                    placement="bottomLeft"
                    showArrow={false}
                    overlayClassName={popupClassName}
                    align={{ offset: [0, 0] }}
                    open={visible}
                    onOpenChange={visible => {
                        // open 由有子菜单的一级导航触发, hide 由 popover 和 没有子菜单的一级导航触发
                        if (!visible) {
                            hideSubmenu();
                        }
                    }}
                    getPopupContainer={() => navRef.current as HTMLDivElement}
                    transitionName={'ant-slide-up'}
                    autoAdjustOverflow={false}
                >
                    <div
                        className={'g-trigger'}
                        onMouseLeave={() => {
                            // 拦截 debounceShowSubmenu 延迟执行的 callback
                            if (!visible && !isMobile) {
                                hideSubmenu();
                            }
                        }}
                    >
                        {navList.map((item: INavItem, index: number) => (
                            <NavLink
                                key={index}
                                item={item}
                                relTarget={'menu'}
                                position={index + 1}
                                level={1}
                                className={cn('g-level1-link', { 'g-active': curIndex === index })}
                                // 移动端需要在子菜单展示后再跳转
                                {...(isMobile ? {
                                    onClick: e => handleMobileClick(e as MouseEvent, index)
                                } : {
                                    onMouseEnter: () => handleMouseEnter(index),
                                    onClick: () => close()
                                })}
                            />
                        ))}
                    </div>
                </Popover>
            )}
        </div>
    );
}
